import _ from 'lodash';
import moment from 'moment';
import autobind from 'autobind-decorator';

import {
	autorun,
	observable,
	action,
	observe,
	computed,
	runInAction,
	reaction,
	extendObservable,
	toJS
} from 'mobx';

import config from 'client/shared/config';
import {
	apiStore,
	BaseStore,
	userStore
} from 'common/stores';

import {
	RATING_CATEGORIES,
	RATING_SCALE,
	RATING_LABELS,
	TIERS
} from './artist-ratings-constants';

const { routes, paths } = config;

class ArtistStore extends BaseStore {
	apiRoute = routes.artists;

	status = {
		PENDING: 'PENDING',
		ACTIVE: 'ACTIVE',
		INACTIVE: 'INACTIVE',
		CANCELLED: 'CANCELLED'
	};

	@observable
	_newArtistPhotoData = '';
	
	@observable
	_isExclusive = true;
	
	@observable
	_isFeatured = false;
	
	@observable
	_featuredBio = '';

	@observable
	ratingCategories = RATING_CATEGORIES.map((label) => {
		return { label, score: 0 };
	});

	@observable
	ratingScale = RATING_SCALE;

	@observable
	statusFilter = {
		status: this.status.ACTIVE
	};

	@observable
	tierFilter = {
		tier: TIERS.TIER_1
	};

	constructor () {
		super();
		reaction(() => apiStore.data.artist, this.onNewData);
		observe(this, '_newArtistPhotoData', this.saveArtistPhoto);
	}

	@computed
	get data () {
		return {
			artist: this._data.item
		};
	}

	@computed
	get artists () {
		return this._items;
	}

	set artists (artists) {
		this._items = artists;
    }
	
	@computed
	get isExclusive () {
		return this._isExclusive;
	}

	set isExclusive (isExclusive) {
		this._isExclusive = isExclusive;
    }

	@computed
	get pendingArtists () {
		return this.artistUserList
			.filter(({ status }) => status === this.status.PENDING);
	}

	@computed
	get submissionsList () {
		let categorizedList = {};

		this.pendingArtists.forEach((artistUser) => {
			const regDate = moment(artistUser.createdAt).format('MM/DD/YYYY');

			if (_.isEmpty(categorizedList[regDate])) {

				categorizedList[regDate] = [];
			}
			if (artistUser.songs.length >= 1) {
				categorizedList[regDate].push(artistUser);
			}
		});

		categorizedList = _.reverse(categorizedList);

		return categorizedList;
	}

	@computed
	get filteredArtistList () {
		const { statusFilter = {}, tierFilter = {} } = this;
		let filters = {};
		if (statusFilter.status != 'ALL') filters.status = statusFilter.status;
		if (tierFilter.tier != 'ALL') filters.tier = tierFilter.tier;
		console.log('filters',filters)
		console.log('origfilters', { ...toJS(statusFilter),
			 	...toJS(tierFilter) })

		let filteredArtistList = _.filter(
			this.artists,
			filters
			// { ...toJS(statusFilter),
			// 	...toJS(tierFilter) }
		);
		if (filters == {}) {
			console.log('aaa')
			filteredArtistList = this.artists;
		}

		return filteredArtistList;
	}

	@computed
	get totalScore () {
		let numSongs = 0;
		try {
			numSongs = this.data.artist.get('songs').length || 0;
			// numSongs = this.data.songs.length || 0;
		} catch {
			console.log('warning: failed to find numSongs!')
		}

		let songScore = 0;
		if (numSongs >= 53) {
			songScore = 4;
		} else if (numSongs < 53 && numSongs >= 27) {
			songScore = 3;
		} else if (numSongs < 27 && numSongs >= 14) {
			songScore = 2;
		} else if (numSongs < 14 && numSongs >= 5) {
			songScore = 1;
		}
		return (
			_(this.data.artist.get('scoreMatrix'))
				.filter((scoreItem) => scoreItem.label != 'Catalog Size')
				.map((scoreItem) => scoreItem.score)
				.reduce((sum, val) => sum + val, 0)
			+ songScore
		);
	}

	@computed
	get artistImage () {
		return this.getArtistImage();
	}

	@computed
	get alphabeticArtists () {
		return _(this.artists)
			.filter(({ status }) => status === this.status.ACTIVE)
			.sortBy('name')
			.value();
	}

	@computed
	get featuredArtists () {
		return this.getFeaturedArtists();
	}

	getFeaturedArtists () {
		return _(this.artists)
			// .filter(({ status }) => status === this.status.ACTIVE)
			.filter(({ isFeatured}) => isFeatured === true)
			.value();
	}

	@computed
	get newReleases () {
		return _(this.artists)
			.filter(({ status }) => status === this.status.ACTIVE)
			.sortBy('createdAt')
			.reverse()
			.slice(0, 9)
			.value();
	}

	@computed
	get tier () {
		const totalScore = this.totalScore;

		if (totalScore <= 4) {

			return TIERS.TIER_0;
		}

		if (totalScore === 5) {

			return TIERS.TIER_5;
		}

		if (totalScore === 6 || totalScore === 7) {

			return TIERS.TIER_4;
		}

		if (totalScore >= 8 && totalScore <= 10) {

			return TIERS.TIER_3;
		}

		if (totalScore >= 11 && totalScore <= 13) {

			return TIERS.TIER_2;
		}

		if (totalScore >= 14) {

			return TIERS.TIER_1;
		}
	}

	@action
	setArtistPhotoData (imageData) {
		this._newArtistPhotoData = imageData;
	}

	@computed
	get artistUserList () {
		if (_.isEmpty(this.artists)) {

			return [];
		}

		return _(this.artists)
			.sortBy('createdAt')
			.map((artist) => {
				const user = userStore.getItemByID({
					_id: artist.userID
				});

				return { ...user, ...artist };
			})
			.value();
	}

	@autobind
	@action
	onNewData (change) {
		this.data.artist.replace(toJS(change));
		this.isLoading = false;
	}

	@action
	setArtistPhotoData (imageData) {
		this._newArtistPhotoData = imageData;
	}

	@action
	async saveArtist (change = null, opts = {}) {

		if (_.isString(this.data.artist.get('_id')) && _.isEmpty(change)) {
			
			change = this.data.artist.toPOJO();
			change.type = this.changeTypes.UPDATE;
		}

		await this.updateItem(change, opts);
	}

	@action
	async createArtist (artist, user, s3Path) {
		this.isLoading = true;

		try {

			const formData = new FormData();

			formData.append('s3Path', s3Path);
			formData.append('artist', JSON.stringify(artist));
			formData.append('user', JSON.stringify(user));

			artist.uploadSongs
				.forEach(
					(song) => formData.append('songs[]', song, song.name)
				);

			await this.saveData(formData, routes.createArtist);
		} catch (err) {

			runInAction('Update isLoading create artist', () => {
				this.isLoading = false;
			});

			throw err;
		}
	}

	@action
	async saveArtistPhoto (change) {
		const route = config.routes.artistPhotoUpload.replace(/\:_id/, this.data.artist.get('_id'));
		const payload = {
			imgData: change.newValue,
			path: `band-photos/${userStore.userNameForFilePath}`
		};

		try {

			const { data: { photos } } = await this.ax.post(route, payload);
			this.data.artist.set('photos', photos);
		} catch (err) {

			console.log('[ERROR: ArtistStore.saveArtistPhoto]', err);
		}
	}

	@action
	removeArtistPhoto (photo) {
		const photos = this.data.artist.get('photos');
		photos.remove(photo);
		this.data.artist.set('photos', photos);
		this.saveArtist();
	}

	@action
	async approveArtist (artistID) {
		const data = await this.updateItem({
			status: this.status.ACTIVE
		});

		runInAction('Update with fetched user list', this.fetchItems);
	}

	@action
	async setMoodList(moods, artist) {
		const route = config.routes.setArtistMoods.replace(/\:id/, artist._id);
		const payload = {
			moods: moods
		};

		try {
			const { data: { list, moods: updatedMoods } = {} } = await this.ax.put(route, payload);
			artist.moods = list || updatedMoods || moods;
		} catch (err) {

			console.log('[ERROR: ArtistStore.setMoodList]', err);
		}
	}

	@action
	async addMood(mood, artist) {
		const route = config.routes.addArtistMood.replace(/\:id/, artist._id);
		const payload = {
			name: mood
		};

		try {
			const { data: { moods } } = await this.ax.post(route, payload);
			artist.moods = moods;
		} catch (err) {

			console.log('[ERROR: ArtistStore.addMood]', err);
		}
	}

	@action
	async deleteMoodByName(mood, artist) {
		const route = config.routes.deleteArtistMoodByName.replace(/\:id/, artist._id).replace(/\:name/, mood);

		try {
			const { data: { list, moods: updatedMoods } = {} } = await this.ax.delete(route);
			artist.moods = list || updatedMoods || [];
		} catch (err) {

			console.log('[ERROR: ArtistStore.deleteMoodByName]', err);
		}
	}

	@action
	setCategoryRating (label, score) {
		const itemIndex = _.findIndex(this.data.artist.get('scoreMatrix'), (item) =>  item.label === label);
		const scoreMatrix = this.data.artist.get('scoreMatrix');
		scoreMatrix[itemIndex].score = score;
		this.data.artist.set('scoreMatrix', scoreMatrix);
	}

	@action
	setStatusFilter (status) {
		this.statusFilter = { status };
	}

	@action
	setTierFilter (tierKey) {
		const tier = (tierKey == 'ALL' ? tierKey : TIERS[tierKey] || TIERS.TIER_1);
		this.tierFilter = { tier };
	}

	@action
	async addFeaturedArtist (artist) {
		const featuredArtists = this.getFeaturedArtists();
		const artists = this.artists.slice();

		
		if(featuredArtists.length > 0) {
			if(featuredArtists[0].id !== artist.id) {
				featuredArtists[0].isFeatured = false; 
				await this.saveArtist(
					{
						...featuredArtists[0],
						type: this.changeTypes.UPDATE
					},
					{
						silent: true
					}
				);
			}
		 }

		artist.isFeatured = true;
		await this.saveArtist(
			{
				...artist,
				type: this.changeTypes.UPDATE
			},
			{
				silent: true
			}
		);

		this.artists = artists;
		this.fetchItems();

	}

	@action
	setCurrent (newID) {
		if (_.isEmpty(newID)) {

			this.clearDataItem();
			userStore.setCurrent(null);
			return;
		}

		const artist = this.artists.find(({ _id }) => _id === newID);
		this.data.artist.replace(artist);

		if (_.isEmpty(this.data.artist.get('scoreMatrix'))) {

			this.data.artist.set('scoreMatrix', toJS(this.ratingCategories));
		}

		userStore.setCurrent(this.data.artist.get('userID'));
    }
	

	getArtistImage (artist) {
		const { userID: _id } = (artist || this.data.artist.toPOJO());
		const user = userStore.getItemByID({ _id });

		if (!user) {

			return '';
		}

		return `${paths.profileImgBasePath}/${user.profileImage}?ts=${new Date().getTime()}`;
	}

	getArtistByID (value) {
		// return  this.getItemByID({ _id });

		if (_.isString(value)) {

			return _.first(this._items.filter(({ _id }) => _id === value));
		}

		if (_.isObject(value) && _.isArray(value.slice())) {

			return _(value.slice())
				.map((_id) => this.getArtistByID(_id))
				.compact()
				.value();
		}
	}

	async typeAheadSearch (search) {
		if (search.length < 3) {

			return [];
		}

		const route = routes.searchGenres.replace(/\:search/, search),
			response = await this.ax.get(route);

		return response.data.sort(function(a, b) {
			const labelA = a.label.toUpperCase();
			const labelB = b.label.toUpperCase();

			if (labelA < labelB) {

				return -1;
			}

			if (labelA > labelB) {

				return 1;
			}

			return 0;
		});
	}

	getRatingLabel (type, rating) {
		return RATING_LABELS[type][rating - 1];
	}

	getSubmittedArtistByID (_id) {
		return this.pendingArtists.find(({ _id }) => _id === _id);
	}

	getArtistByUserID (userID) {
		return _.first(this.artists.filter((artist) => artist.userID === userID));
	}
}

const artistStore = window.artistStore = new ArtistStore();
export default artistStore;
